/**
 * Form validation rules and the field/record validators built on them.
 *
 * Display formatting used to live here too; it is now in formatters.ts.
 */

// Validation utilities
export interface ValidationRule {
  test: (value: string) => boolean;
  message: string;
}

export interface FieldValidation {
  required?: boolean;
  rules?: ValidationRule[];
}

// Validation rules
export const validationRules = {
  // Name validation
  minLength: (min: number): ValidationRule => ({
    test: (value) => value.length >= min,
    message: `Must be at least ${min} characters`,
  }),

  maxLength: (max: number): ValidationRule => ({
    test: (value) => value.length <= max,
    message: `Must be no more than ${max} characters`,
  }),

  lettersAndSpacesOnly: (): ValidationRule => ({
    test: (value) => /^[a-zA-Z\s]+$/.test(value),
    message: "Only letters and spaces allowed",
  }),

  // Email validation
  email: (): ValidationRule => ({
    test: (value) => /\S+@\S+\.\S+/.test(value),
    message: "Email is invalid",
  }),

  // Password validation
  password: (min: number = 8): ValidationRule => ({
    test: (value) => value.length >= min,
    message: `Password must be at least ${min} characters`,
  }),

  // Regex Validation
  passwordStrong: (): ValidationRule => ({
    test: (value) =>
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])/.test(value),
    message:
      "Password must contain uppercase, lowercase, number, and special character",
  }),

  // Sets validation for exercise training
  minReps: (): ValidationRule => ({
    test: (value) => {
      const num = parseInt(value);
      return !isNaN(num) && num >= 1;
    },
    message: "Reps must be at least 1",
  }),

  minWeight: (): ValidationRule => ({
    test: (value) => {
      if (!value || value === "") return true;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 1;
    },
    message: "Weight must be at least 1 kg",
  }),

  positiveNumber: (): ValidationRule => ({
    test: (value) => {
      if (!value || value === "") return true;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    },
    message: "Must be a positive number",
  }),
};

// Validation function
export const validateField = (
  value: string,
  fieldName: string,
  validation: FieldValidation
): string | null => {
  // Check required
  if (validation.required && !value) {
    return `${fieldName} is required`;
  }

  // Skip other validations if empty and not required
  if (!value && !validation.required) {
    return null;
  }

  // Run validation rules
  if (validation.rules) {
    for (const rule of validation.rules) {
      if (!rule.test(value)) {
        return rule.message;
      }
    }
  }

  return null;
};

// Validate multiple fields at once
export const validateFields = (
  formData: Record<string, string>,
  validations: Record<string, FieldValidation>
): Record<string, string> => {
  const errors: Record<string, string> = {};

  Object.keys(validations).forEach((fieldKey) => {
    const value = formData[fieldKey] || "";
    const fieldName = fieldKey
      .replace("_", " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    const error = validateField(value, fieldName, validations[fieldKey]);

    if (error) {
      errors[fieldKey] = error;
    }
  });

  return errors;
};
